/* "Where was I?" — remembers where Qurʾān audio stopped, so an accidental
   Stop, a wrong tap (starting another surah or reciter), a lost connection or
   iOS killing the app never loses your place.

   Keeps the last few tracks (per reciter + surah/juz/recording) with their
   exact position. A track that played to its end is dropped: there is nothing
   to resume. Device-only — it's a convenience, not progress data. */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { usePlayer } from './audioEngine';

export interface ResumePoint {
  key: string;
  reciterId: string;
  surah: number;
  juz?: number;
  clip?: string;
  positionMs: number;
  durationMs: number;
  verseKey: string | null;
  at: number;
  /** why it stopped: 'stop' (Stop button), 'replaced' (something else was started), 'error', 'paused' (still loaded, or app closed while paused/playing) */
  reason: 'stop' | 'replaced' | 'error' | 'paused';
}

const MAX = 5;
const SAVE_EVERY_MS = 5_000;
const DONE_WITHIN_MS = 8_000;

export const useResume = create<{ points: ResumePoint[]; dismiss: (key: string) => void }>()(
  persist((set) => ({
    points: [],
    dismiss: (key) => set((s) => ({ points: s.points.filter((p) => p.key !== key) })),
  }), { name: 'ftu_resume' }),
);

const keyOf = (reciterId: string, surah: number, juz?: number, clip?: string) => `${reciterId}|${clip ? `c:${clip}` : juz ? `j:${juz}` : `s:${surah}`}`;

type P = ReturnType<typeof usePlayer.getState>;
const currentKey = (st: P) => (st.surah && st.track ? keyOf(st.reciterId, st.surah, st.track.juz, st.track.clip?.id) : null);

function save(st: P, reason: ResumePoint['reason']) {
  if (!st.surah || !st.track) return;
  const key = currentKey(st)!;
  const finished = st.durationMs > 0 && st.positionMs >= st.durationMs - DONE_WITHIN_MS;
  useResume.setState((s) => {
    const rest = s.points.filter((p) => p.key !== key);
    if (finished || st.positionMs < 3_000) return { points: rest }; // done, or barely started
    const point: ResumePoint = {
      key, reciterId: st.reciterId, surah: st.surah!, juz: st.track!.juz, clip: st.track!.clip?.id,
      positionMs: st.positionMs, durationMs: st.durationMs, verseKey: st.current.verseKey, at: Date.now(), reason,
    };
    return { points: [point, ...rest].slice(0, MAX) };
  });
}

/** The newest resume point that isn't what's loaded right now. */
export function latestResumable(points: ResumePoint[], st: P) {
  const live = currentKey(st);
  return points.find((p) => p.key !== live) ?? null;
}

export async function resumeFrom(p: ResumePoint) {
  await usePlayer.getState().play(p.surah, { reciterId: p.reciterId, juz: p.juz, clip: p.clip, startAtMs: Math.max(0, p.positionMs - 3_000) });
}

let started = false;
/** Mount once: follows the player and records where things stop. */
export function trackResumePoints() {
  if (started) return;
  started = true;
  let lastSave = 0;
  let prev = usePlayer.getState();
  usePlayer.subscribe((st) => {
    const was = prev;
    prev = st;
    const wasKey = currentKey(was);
    // Stop button: track cleared.
    if (wasKey && !st.track && st.status === 'idle') { save(was, 'stop'); return; }
    // Something else started over a track that was in progress.
    if (wasKey && st.status === 'loading' && (st.surah !== was.surah || st.reciterId !== was.reciterId)) { save(was, 'replaced'); return; }
    if (wasKey && st.status === 'error' && was.status !== 'error') { save(was, 'error'); return; }
    // Periodic save while playing/paused — covers the app being closed or killed.
    if (currentKey(st) && (st.status === 'playing' || st.status === 'paused' || st.status === 'gap') && Date.now() - lastSave > SAVE_EVERY_MS) {
      lastSave = Date.now();
      save(st, 'paused');
    }
  });
}
